/**
 * AdmissionsPipeline — admin kanban board for enrollment leads.
 *
 * Stages: Inquiry → Tour booked → Application sent → Application received
 *       → Offer → Enrolled → Active (or Withdrawn).
 *
 * Click-to-advance per row; full HTML5 drag-and-drop between columns.
 * Filters: room, search by name/email. Summary KPIs at the top.
 *
 * Mirrors AdminTranslationFeedback patterns (calm cards, status pills,
 * existing AdminHeader). NO redesign — reuses the existing design system.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, ArrowRight, Plus, Send } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

const STAGES = [
  "inquiry",
  "tour_booked",
  "application_sent",
  "application_received",
  "offer",
  "enrolled",
  "active",
  "withdrawn",
];

function stageLabel(t, stage) {
  return t(`admissions.pipeline.stage_${stage}`);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return iso;
  }
}

export default function AdmissionsPipeline() {
  const { t } = useI18n();
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roomFilter, setRoomFilter] = useState("");
  const [search, setSearch] = useState("");
  const [draggingId, setDraggingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [leadsRes, sumRes, roomsRes] = await Promise.all([
        api.get("/admin/admissions/leads", {
          params: roomFilter ? { room_id: roomFilter } : {},
        }),
        api.get("/admin/admissions/summary"),
        api.get("/admissions/center/rooms"),
      ]);
      setLeads(leadsRes.data || []);
      setSummary(sumRes.data);
      setRooms(roomsRes.data || []);
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return leads;
    const q = search.toLowerCase();
    return leads.filter(
      (l) =>
        l.parent_name.toLowerCase().includes(q) ||
        l.parent_email.toLowerCase().includes(q) ||
        l.child_name.toLowerCase().includes(q) ||
        (l.desired_room_name || "").toLowerCase().includes(q),
    );
  }, [leads, search]);

  const byStage = useMemo(() => {
    const grouped = {};
    for (const s of STAGES) grouped[s] = [];
    for (const lead of filtered) {
      const s = STAGES.includes(lead.stage) ? lead.stage : "inquiry";
      grouped[s].push(lead);
    }
    return grouped;
  }, [filtered]);

  const advance = async (lead, newStage) => {
    if (lead.stage === newStage) return;
    try {
      const r = await api.patch(`/admin/admissions/leads/${lead.id}/stage`, {
        stage: newStage,
      });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? r.data : l)));
      toast.success(`${lead.child_name} → ${stageLabel(t, newStage)}`);
      const sumRes = await api.get("/admin/admissions/summary");
      setSummary(sumRes.data);
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    }
  };

  return (
    <div data-testid="admin-admissions" className="space-y-8">
      <AdminHeader
        eyebrow={t("admissions.nav_admin")}
        title={t("admissions.pipeline.title")}
        action={
          <Link
            to="/admin/admissions/documents"
            data-testid="admin-admissions-docs-link"
            className="text-xs font-semibold text-[#1F3A2A] underline underline-offset-4"
          >
            {t("admissions.documents_admin.title")}
          </Link>
        }
      />
      <p className="text-sm text-[#6B7280] -mt-4 max-w-2xl">
        {t("admissions.pipeline.subtitle")}
      </p>

      {summary && (
        <section
          data-testid="admin-admissions-summary"
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          <KpiTile
            label={t("admissions.pipeline.summary_open")}
            value={summary.total_open}
            testid="admin-admissions-kpi-open"
          />
          <KpiTile
            label={t("admissions.pipeline.summary_inquiries_7d")}
            value={summary.inquiries_last_7_days}
            testid="admin-admissions-kpi-inquiries-7d"
          />
          <KpiTile
            label={t("admissions.pipeline.summary_enrolled_30d")}
            value={summary.enrolled_last_30_days}
            testid="admin-admissions-kpi-enrolled-30d"
          />
          <KpiTile
            label={t("admissions.pipeline.stage_offer")}
            value={summary.by_stage?.offer || 0}
            testid="admin-admissions-kpi-offer"
          />
        </section>
      )}

      <section className="rounded-2xl border border-[#EEF0F4] bg-white p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder={t("admissions.pipeline.search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-admissions-search"
            className="input-soft flex-1 min-w-[200px]"
          />
          <select
            value={roomFilter}
            onChange={(e) => setRoomFilter(e.target.value)}
            data-testid="admin-admissions-room-filter"
            className="input-soft"
          >
            <option value="">{t("admissions.pipeline.filter_all_rooms")}</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} {r.age_band ? `· ${r.age_band}` : ""}
              </option>
            ))}
          </select>
        </div>
      </section>

      {loading ? (
        <p className="text-sm text-[#6B7280] italic">{t("admissions.common.loading")}</p>
      ) : (
        <section
          data-testid="admin-admissions-board"
          className="overflow-x-auto pb-4"
        >
          <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-4 min-w-full">
            {STAGES.map((stage) => (
              <StageColumn
                key={stage}
                stage={stage}
                label={stageLabel(t, stage)}
                leads={byStage[stage]}
                onDrop={(leadId) => {
                  const lead = leads.find((l) => l.id === leadId);
                  if (lead) advance(lead, stage);
                }}
                onDragStart={(id) => setDraggingId(id)}
                onDragEnd={() => setDraggingId(null)}
                draggingId={draggingId}
                t={t}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function KpiTile({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#EEF0F4] bg-white p-5"
    >
      <div className="eyebrow">{label}</div>
      <div className="mt-2 font-display text-3xl text-[#1A1B2E]">{value}</div>
    </div>
  );
}

function StageColumn({ stage, label, leads, onDrop, onDragStart, onDragEnd, draggingId, t }) {
  const [hovering, setHovering] = useState(false);
  return (
    <div
      data-testid={`admin-admissions-column-${stage}`}
      onDragOver={(e) => {
        e.preventDefault();
        setHovering(true);
      }}
      onDragLeave={() => setHovering(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHovering(false);
        const id = e.dataTransfer.getData("text/plain");
        if (id) onDrop(id);
      }}
      className={
        "rounded-2xl border bg-[#F7F8FC] p-3 min-h-[400px] transition-colors " +
        (hovering ? "border-[#1F3A2A] bg-[#E6EFE8]/40" : "border-[#EEF0F4]")
      }
    >
      <div className="flex items-center justify-between px-1 pb-3">
        <h3 className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#1F3A2A]">
          {label}
        </h3>
        <span className="text-xs font-mono text-[#6B7280]">{leads.length}</span>
      </div>
      <div className="space-y-2">
        {leads.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] italic px-2 py-3">
            {t("admissions.pipeline.empty_stage")}
          </p>
        ) : (
          leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              dragging={draggingId === lead.id}
              t={t}
            />
          ))
        )}
      </div>
    </div>
  );
}

function LeadCard({ lead, onDragStart, onDragEnd, dragging, t }) {
  const navigate = useNavigate();
  return (
    <article
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", lead.id);
        onDragStart(lead.id);
      }}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        // Don't trigger nav on a drag operation
        if (e.defaultPrevented) return;
        navigate(`/admin/admissions/leads/${lead.id}`);
      }}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/admin/admissions/leads/${lead.id}`);
        }
      }}
      data-testid={`admin-admissions-lead-${lead.id}`}
      className={
        "rounded-xl border bg-white p-3.5 cursor-pointer active:cursor-grabbing transition-all " +
        (dragging ? "opacity-50 border-[#1F3A2A]" : "border-[#EEF0F4] hover:border-[#1F3A2A]/40 hover:shadow-[0_4px_12px_rgba(16,24,40,0.06)]")
      }
    >
      <Link
        to={`/admin/admissions/leads/${lead.id}`}
        className="block font-display text-base text-[#1A1B2E] leading-snug"
        onClick={(e) => e.stopPropagation()}
      >
        {lead.child_name}
      </Link>
      <p className="mt-1 text-xs text-[#6B7280]">{lead.parent_name}</p>
      <p className="mt-1 text-xs text-[#9CA3AF]">{lead.parent_email}</p>
      {lead.desired_room_name && (
        <span className="mt-2 inline-block text-[10px] tracking-[0.1em] uppercase font-semibold text-[#1F3A2A] bg-[#E6EFE8] rounded-full px-2 py-0.5">
          {lead.desired_room_name}
        </span>
      )}
      <p className="mt-2 text-[10px] text-[#9CA3AF]">
        {t("admissions.pipeline.received", { date: formatDate(lead.received_at) })}
      </p>
      {lead.stage === "tour_booked" && lead.tour_date && (
        <p className="mt-1 text-[10px] text-[#8C6D1F]">
          {t("admissions.pipeline.tour_date", { date: formatDate(lead.tour_date) })}
        </p>
      )}
      {lead.stage === "offer" && lead.offer_sent_at && (
        <p className="mt-1 text-[10px] text-[#8C6D1F]">
          {t("admissions.pipeline.offer_sent", { date: formatDate(lead.offer_sent_at) })}
        </p>
      )}
    </article>
  );
}
